import { FilterDropdown } from '@/shared/components/FilterDropdown'
import { AssignTaskDialog } from '@/shared/components/legal-documents/AssignTaskDialog'
import { TaskDetailDialog } from '@/shared/components/legal-documents/TaskDetailDialog'
import { DocumentLinkPopover } from '@/shared/components/projects/project-profile/DocumentLinkPopover'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useAreas } from '@/shared/hooks/useAreas'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useRegions } from '@/shared/hooks/useRegions'
import { api, StatusType, type Task, TaskPhase } from '@/shared/lib/api'
import { useCan } from '@/shared/lib/authorization'
import { useUser } from '@/shared/store/auth.store'
import { ADMIN_ROLE_NAME } from '@/shared/constants'
import { useProjectPermissions } from '@/shared/hooks/projects/useProjectPermissions'
import { useExportReport } from '@/shared/hooks/projects/useExportReport'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { createColumnHelper } from '@tanstack/react-table'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import { Badge } from '@workspace/ui/components/Badge'
import { Button } from '@workspace/ui/components/Button'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import { DataTable, type DataTableSorting } from '@workspace/ui/components/DataTable'
import { Menu, MenuHeader, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { Tabs, TabList, Tab, TabPanel } from '@workspace/ui/components/Tabs'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { toast } from '@workspace/ui/components/Sonner'
import { Tooltip, TooltipTrigger } from '@workspace/ui/components/Tooltip'
import { cn } from '@workspace/ui/lib/utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { FileDown, Loader2, MoreHorizontal, PlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Focusable } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { SecurityLevel } from '@/shared/lib/api'
import { PreviewSearchItem } from '@/shared/components/previewSearchItem'

// Extend dayjs with UTC plugin
dayjs.extend(utc)

export const Route = createFileRoute('/projects/$id/_pathlessLayout/tasks')({
    validateSearch: z.object({
        taskId: z.string().optional(),
        tab: z.enum(['all', 'assigned_to_me', 'assigned_by_me']).optional(),
        q: z.string().optional(),
        page: z.number().catch(1).optional(),
        pageSize: z.number().catch(10).optional(),
        sortBy: z.string().optional(),
        sortDirection: z.string().optional(),
        regions: z.string().optional(),
        areas: z.string().optional(),
        status: z.string().optional(),
        phase: z.string().optional(),
        priorities: z.string().optional(),
        assignees: z.string().optional(),
        formats: z.string().optional(),
    }),
    component: TasksPage,
    beforeLoad: requirePermissionBeforeLoad('view', 'project'),
})

const columnHelper = createColumnHelper<Task>()

function TasksPage() {
    const { t } = useTranslation()
    const { id } = Route.useParams() as { id: string }
    const navigate = useNavigate({ from: Route.fullPath })
    const { taskId } = Route.useSearch()

    const currentUser = useUser()
    const isAdmin = currentUser?.role?.name === ADMIN_ROLE_NAME
    const globalView = useCan('view', 'project')
    const globalOperateTask = useCan('operate', 'task')
    const globalOperateProject = useCan('operate', 'project')
    const globalExportProject = useCan('export', 'project')
    const projectPerm = useProjectPermissions(id)
    const hasQueried = projectPerm.hasQueriedApi

    const canManage =
        isAdmin ||
        (globalOperateTask && hasQueried && projectPerm.canManage) ||
        (globalOperateProject && hasQueried && projectPerm.canManage)

    const canInput =
        isAdmin ||
        (globalOperateTask && hasQueried && (projectPerm.canManage || projectPerm.canInput)) ||
        (globalOperateProject && hasQueried && (projectPerm.canManage || projectPerm.canInput))

    const canView =
        isAdmin ||
        (globalView && hasQueried && (projectPerm.canView || projectPerm.canManage || projectPerm.canInput)) ||
        canManage ||
        canInput

    const canOperateTasks =
        isAdmin ||
        globalOperateTask ||
        globalOperateProject ||
        (hasQueried && (projectPerm.canManage || projectPerm.canInput))

    const canExportProject = isAdmin || globalExportProject

    const [rowSelection, setRowSelection] = useState({})
    const searchParams = Route.useSearch()

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const activeTab = searchParams.tab || 'all'
    const searchQuery = searchParams.q || ''
    const regionValues = searchParams.regions ? searchParams.regions.split(',') : []
    const areaValues = searchParams.areas ? searchParams.areas.split(',') : []
    const statusValues = searchParams.status ? searchParams.status.split(',') : []
    const phaseValues = searchParams.phase as TaskPhase | null
    const priorityValues = searchParams.priorities ? searchParams.priorities.split(',') : []
    const assigneeValues = searchParams.assignees ? searchParams.assignees.split(',') : []
    const textFormatValues = searchParams.formats ? searchParams.formats.split(',') : []
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : null

    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setActiveTab = (tab: 'all' | 'assigned_to_me' | 'assigned_by_me') => updateSearch({ tab, page: 1 })
    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setRegionValues = (regions: string[]) =>
        updateSearch({ regions: regions.length > 0 ? regions.join(',') : undefined, page: 1 })
    const setAreaValues = (areas: string[]) =>
        updateSearch({ areas: areas.length > 0 ? areas.join(',') : undefined, page: 1 })
    const setStatusValues = (status: string[]) =>
        updateSearch({ status: status.length > 0 ? status.join(',') : undefined, page: 1 })
    const setPhaseValues = (phase: TaskPhase | null) => updateSearch({ phase: phase || undefined, page: 1 })
    const setPriorityValues = (priorities: string[]) =>
        updateSearch({ priorities: priorities.length > 0 ? priorities.join(',') : undefined, page: 1 })
    const setAssigneeValues = (assignees: string[]) =>
        updateSearch({ assignees: assignees.length > 0 ? assignees.join(',') : undefined, page: 1 })
    const setTextFormatValues = (formats: string[]) =>
        updateSearch({ formats: formats.length > 0 ? formats.join(',') : undefined, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [isAssignTaskOpen, setIsAssignTaskOpen] = useState(false)
    const [selectedTask, setSelectedTask] = useState<Task | null>(null)
    const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false)
    const [assignTaskMode, setAssignTaskMode] = useState<'assignment' | 'order'>('assignment')

    // Fetch single task if taskId is provided in URL
    const { data: detailTask, isSuccess: isDetailSuccess } = useQuery({
        queryKey: ['task', taskId],
        queryFn: () => api.task.findOne(taskId!),
        enabled: !!taskId,
    })

    useEffect(() => {
        if (isDetailSuccess && detailTask) {
            setSelectedTask(detailTask)
            setIsTaskDetailOpen(true)
            // Clear taskId from search params to keep URL clean
            navigate({
                search: (prev: any) => {
                    const { taskId: _, ...rest } = prev
                    return rest
                },
                replace: true,
            })
        }
    }, [isDetailSuccess, detailTask, navigate])

    const [isRegionFilterOpen, setIsRegionFilterOpen] = useState(false)
    const [isProjectSelectOpen, setIsProjectSelectOpen] = useState(false)
    const [isAreaFilterOpen, setIsAreaFilterOpen] = useState(false)
    const [isAssigneeFilterOpen, setIsAssigneeFilterOpen] = useState(false)

    const debouncedSearch = useDebounce(searchQuery, 300)

    const handleDeleteMultiple = async () => {
        const taskIds = Object.keys(rowSelection)
        if (taskIds.length === 0) return

        await deleteMutation.mutateAsync(taskIds)
    }

    const { data, isLoading, refetch } = useQuery({
        queryKey: [
            'tasks',
            debouncedSearch,
            regionValues,
            areaValues,
            statusValues,
            phaseValues,
            priorityValues,
            assigneeValues,
            textFormatValues,
            activeTab,
            currentPage,
            pageSize,
            sorting,
            id,
            currentUser?.id,
        ],
        queryFn: () =>
            api.task.list({
                page: currentPage,
                take: pageSize,
                q: debouncedSearch || undefined,
                status:
                    statusValues.filter(s => s !== 'overdue').length > 0
                        ? (statusValues.filter(s => s !== 'overdue').join(',') as StatusType)
                        : undefined,
                isOverdue: statusValues.includes('overdue') ? true : undefined,
                regionIds: regionValues.length > 0 ? regionValues.join(',') : undefined,
                areaIds: areaValues.length > 0 ? areaValues.join(',') : undefined,
                priorities: priorityValues.length > 0 ? priorityValues.join(',') : undefined,
                taskType: textFormatValues.length > 0 ? textFormatValues.join(',') : undefined,
                assignedTo: assigneeValues.length > 0 ? assigneeValues.join(',') : undefined,
                tab: activeTab === 'all' ? undefined : activeTab,
                phase: phaseValues,
                projectId: id,
                orderBy: sorting?.sortBy,
                order: sorting?.sortDirection?.toUpperCase() as 'ASC' | 'DESC' | undefined,
            }),
        enabled: canView,
        meta: { skipGlobalErrorHandling: true },
    })

    // Delete task mutation
    const deleteMutation = useMutation({
        mutationFn: async (taskIds: string[]) => {
            console.log(taskIds)

            // Delete tasks in parallel
            await Promise.all(taskIds.map(id => api.task.delete(id)))
        },
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('assignTask.list.deleteSuccess.description'),
            })
            // Refresh the list and clear selection
            refetch()
            setRowSelection({})
        },
        onError: error => {
            toast.error({
                title: t('common.status.error'),
                description: t('assignTask.list.deleteError.description'),
            })
            console.error('Delete error:', error)
        },
    })

    const exportReportMutation = useExportReport()

    const handleExportReport = async () => {
        await exportReportMutation.mutateAsync({
            projectIds: [id],
        })
    }

    const statusOptions = [
        { id: 'todo', name: 'Chuẩn bị công việc' },
        { id: 'receiving', name: 'Kiểm tra hồ sơ & tiếp nhận công việc' },
        { id: 'in_progress', name: 'Đang xử lý' },
        { id: 'paused', name: 'Tạm dừng' },
        { id: 'waiting_approval', name: 'Chờ duyệt' },
        { id: 'completed', name: 'Hoàn thành' },
        { id: 'cancelled', name: 'Đã hủy' },
        { id: 'overdue', name: 'Quá hạn' },
    ]

    const textFormatOptions = [
        { id: 'order', name: 'Đặt hàng' },
        { id: 'assignment', name: 'Giao việc' },
    ]

    const priorityOptions = [
        { id: 'high', name: 'Cao' },
        { id: 'medium', name: 'Trung bình' },
        { id: 'low', name: 'Thấp' },
    ]

    const phaseOptions = [
        { id: TaskPhase.PREPARATION, name: 'Chuẩn bị' },
        { id: TaskPhase.IMPLEMENTATION, name: 'Triển khai' },
        { id: TaskPhase.COMPLETION, name: 'Hoàn thiện' },
    ]

    // Fetch users for manager filter dropdown (scoped to project)
    const { data: usersData, isLoading: isLoadingAssignees } = useQuery({
        queryKey: ['projects', id, 'users'],
        queryFn: () => api.project.listUsers(id, { page: 1, take: 100 }),
        enabled: isAssigneeFilterOpen,
    })

    const usersList = Array.isArray(usersData) ? usersData : (usersData?.data ?? [])

    const assigneeOptions = usersList.map((user: any) => ({ id: user.id, name: user.fullName })) || []

    const { data: projects, isLoading: isLoadingProjects } = useQuery({
        queryKey: ['projects'],
        queryFn: () => api.project.list({ page: 1, take: 100 }),
        enabled: isProjectSelectOpen,
    })

    const { data: regions, isLoading: isLoadingRegions } = useRegions({
        enabled: isRegionFilterOpen,
    })

    const { data: areas, isLoading: isLoadingAreas } = useAreas({
        enabled: isAreaFilterOpen,
        regionIds: regionValues.length > 0 ? regionValues.join(',') : undefined,
    })

    const regionOptions = (regions?.data || []).map(region => ({ id: region.id, name: region.name }))
    const areaOptions = (areas?.data || []).map(area => ({ id: area.id, name: area.name }))

    const projectOptions = projects?.data.map(project => ({ name: project.name || '', id: project.id })) || []

    const totalResults = data?.meta?.itemCount || 0

    const filteredTasks = useMemo(() => {
        if (!data?.data) return []
        if (canManage) return data.data
        return data.data.filter(task => {
            const relatedDocs = task.relatedDocuments || []
            if (relatedDocs.length === 0) return true

            return relatedDocs.some(doc => doc.classificationLevel === SecurityLevel.NORMAL)
        })
    }, [data?.data, canManage])

    const columns = [
        columnHelper.display({
            id: 'custom-actions',
            header: '',
            size: 48,
            enableSorting: false,
            meta: { className: '!max-w-[48px] !min-w-[48px] !w-[48px]' },
            cell: ({ row }) => {
                const task = row.original
                return (
                    <MenuTrigger>
                        <Button variant="outline" size="iconSm" className="rounded-full border-foreground size-5">
                            <MoreHorizontal className="size-3!" />
                        </Button>
                        <MenuPopover className="w-[120px]">
                            <Menu>
                                <MenuHeader className="text-foreground">{t('common.actions.operation')}</MenuHeader>
                                <MenuItem
                                    onAction={() => {
                                        setSelectedTask(task)
                                        setIsTaskDetailOpen(true)
                                    }}
                                    className="cursor-pointer"
                                >
                                    {t('common.actions.view')}
                                </MenuItem>
                                {task.canDelete && (
                                    <MenuItem
                                        onAction={() =>
                                            confirm({
                                                title: t('assignTask.list.deleteConfirm.title'),
                                                description: t('assignTask.list.deleteConfirm.description'),
                                                action: {
                                                    label: t('common.actions.delete'),
                                                    onClick: async () => {
                                                        await deleteMutation.mutateAsync([task.id])
                                                    },
                                                },
                                                cancel: {
                                                    label: t('common.actions.cancel'),
                                                },
                                                variant: 'destructive',
                                            })
                                        }
                                        className="cursor-pointer text-destructive"
                                    >
                                        {t('common.actions.delete')}
                                    </MenuItem>
                                )}
                            </Menu>
                        </MenuPopover>
                    </MenuTrigger>
                )
            },
        }),
        columnHelper.accessor('taskCode', {
            header: 'Mã công việc',
            enableSorting: false,
            size: 120,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head text-center' },
            cell: ({ row }) => {
                const task = row.original
                const projectCode = task.project?.code
                return (
                    <>
                        <div className="text-foreground truncate">{task.taskCode}</div>
                        {projectCode && (
                            <div className="text-xs text-neutral-500 truncate" title={projectCode}>
                                {projectCode}
                            </div>
                        )}
                    </>
                )
            },
        }),
        columnHelper.accessor('title', {
            header: 'Công việc',
            enableSorting: true,
            size: 320,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head', noTitleTooltip: true },
            cell: ({ row }) => {
                const task = row.original
                const folderName = task.relatedDocuments?.[0]?.folderPath || ''
                const relatedDocTitle = task.relatedDocuments?.[0]?.title
                const tooltipContent = `${folderName}/${relatedDocTitle}`

                let projectPath = null
                if (folderName) {
                    if (relatedDocTitle) {
                        const folderPathCount = folderName.split('/')

                        projectPath =
                            folderPathCount.length > 1
                                ? `${folderPathCount[0]}/... /${relatedDocTitle}`
                                : `${folderName}/${relatedDocTitle}`
                    } else {
                        projectPath = folderName
                    }
                }

                return (
                    <div
                        className="flex items-start cursor-pointer hover:opacity-80"
                        onClick={() => {
                            setSelectedTask(task)
                            setIsTaskDetailOpen(true)
                        }}
                    >
                        <div className="flex-1 flex flex-col max-w-[250px] truncate">
                            <span className="text-sm text-foreground truncate" title={task.title || 'N/A'}>
                                {task.title || 'N/A'}
                            </span>
                            {projectPath && (
                                <span
                                    className="text-xs text-neutral-500 truncate max-w-full hover:text-neutral-600 w-fit"
                                    title={tooltipContent}
                                >
                                    {projectPath}
                                </span>
                            )}
                        </div>
                        <DocumentLinkPopover
                            relatedTasks={[
                                ...(task?.relatedTasks || []),
                                ...(task.parentTask ? [task.parentTask] : []),
                            ]}
                            onViewTaskDetail={task => {
                                setSelectedTask(task)
                                setIsTaskDetailOpen(true)
                            }}
                        />
                    </div>
                )
            },
        }),
        columnHelper.accessor('taskType', {
            header: 'Hình thức',
            enableSorting: false,
            size: 90,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const value = row.original.taskType
                const labelMap: Record<string, string> = {
                    assignment: 'Giao việc',
                    order: 'Đặt hàng',
                }
                return <span title={labelMap[value || 'order']}>{labelMap[value || 'order']}</span>
            },
        }),
        columnHelper.display({
            id: 'region-area',
            header: 'Vùng - Khu vực',
            enableSorting: false,
            size: 130,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const region = row.original.regions?.map(r => r.name).filter(Boolean) || []
                const area = row.original.areas?.map(a => a.name).filter(Boolean) || []
                return (
                    <div className="flex flex-col py-1">
                        <span className="text-sm font-medium text-foreground leading-5">{region.join(', ')}</span>
                        <span className="text-xs text-muted-foreground leading-4">{area.join(', ')}</span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'priority',
            header: 'Mức ưu tiên',
            enableSorting: false,
            size: 100,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head text-center' },
            cell: ({ row }) => {
                const priority = row.original.priority

                const priorityConfig: Record<
                    'high' | 'medium' | 'low',
                    { backgroundColor: string; textColor: string }
                > = {
                    high: {
                        backgroundColor: 'var(--color-red-500)',
                        textColor: 'white',
                    },
                    medium: {
                        backgroundColor: 'var(--primary)',
                        textColor: 'white',
                    },
                    low: {
                        backgroundColor: 'var(--color-green-500)',
                        textColor: 'white',
                    },
                }

                const config = priorityConfig[priority]
                const priorityLabel: Record<'high' | 'medium' | 'low', string> = {
                    high: 'Cao',
                    medium: 'Trung bình',
                    low: 'Thấp',
                }

                return (
                    <Badge
                        className="h-5 px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{
                            backgroundColor: config.backgroundColor,
                            color: config.textColor,
                        }}
                    >
                        {priorityLabel[priority]}
                    </Badge>
                )
            },
        }),
        columnHelper.display({
            id: 'createdBy',
            header: 'Người giao',
            enableSorting: false,
            size: 100,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const creator = row.original.createdBy
                if (!creator) return <span className="text-xs text-muted-foreground text-center pl-3">-</span>

                return (
                    <div className="flex items-center gap-2">
                        <UserAvatar
                            src={creator.avatar}
                            alt={creator.fullName}
                            fallback={creator.fullName}
                            size="md"
                            className="border-2 border-white"
                        />
                        <span className="text-sm text-foreground truncate max-w-[60px]" title={creator.fullName}>
                            {creator.fullName}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'assignedUsers',
            header: 'Phân công',
            enableSorting: false,
            size: 100,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const assignees = row.original.assignedUsers
                if (!assignees || assignees.length === 0)
                    return <span className="text-xs text-muted-foreground text-center pl-3">-</span>

                return (
                    <div className="flex items-center gap-2">
                        <div className="flex items-center -space-x-2 shrink-0">
                            {assignees.slice(0, 2).map(assignee => (
                                <UserAvatar
                                    key={assignee.id}
                                    src={assignee.avatar}
                                    alt={assignee.fullName}
                                    fallback={assignee.fullName}
                                    size="md"
                                    className="border-2 border-white"
                                />
                            ))}
                            {assignees.length > 2 && (
                                <div
                                    className="relative z-10 size-8 rounded-full bg-gray-110 flex items-center justify-center border-2 border-background"
                                    title={assignees
                                        .slice(2)
                                        .map(a => a.fullName)
                                        .join(', ')}
                                >
                                    <span className="text-xs text-gray-850 font-semibold">+{assignees.length - 2}</span>
                                </div>
                            )}
                        </div>
                        <span className="text-sm text-foreground truncate max-w-[60px]" title={assignees[0].fullName}>
                            {assignees[0].fullName}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'status',
            header: 'Trạng thái',
            enableSorting: false,
            size: 155,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head text-center' },
            cell: ({ row }) => {
                const status = row.original.status

                const statusConfig: Record<
                    | 'todo'
                    | 'receiving'
                    | 'in_progress'
                    | 'paused'
                    | 'waiting_approval'
                    | 'completed'
                    | 'cancelled'
                    | 'overdue',
                    { backgroundColor: string; textColor: string }
                > = {
                    todo: {
                        backgroundColor: 'var(--color-gray-400)',
                        textColor: 'white',
                    },
                    receiving: {
                        backgroundColor: 'var(--color-blue-500)',
                        textColor: 'white',
                    },
                    in_progress: {
                        backgroundColor: 'var(--color-primary)',
                        textColor: 'white',
                    },
                    paused: {
                        backgroundColor: 'var(--color-orange-500)',
                        textColor: 'white',
                    },
                    waiting_approval: {
                        backgroundColor: 'var(--color-indigo-500)',
                        textColor: 'white',
                    },
                    completed: {
                        backgroundColor: 'var(--color-green-500)',
                        textColor: 'white',
                    },
                    cancelled: {
                        backgroundColor: 'var(--color-gray-700)',
                        textColor: 'white',
                    },
                    overdue: {
                        backgroundColor: 'var(--color-red-500)',
                        textColor: 'white',
                    },
                }

                const config = statusConfig[status as keyof typeof statusConfig]
                const statusLabel = t(`assignTask.statuses.${status}`, status)

                return (
                    <Badge
                        className="h-5 px-2 py-0.5 text-xs font-medium rounded-full max-w-[135px]"
                        style={{
                            backgroundColor: config?.backgroundColor || 'var(--color-gray-400)',
                            color: config?.textColor || 'white',
                        }}
                    >
                        <span className="truncate" title={statusLabel}>
                            {statusLabel}
                        </span>
                    </Badge>
                )
            },
        }),
        columnHelper.display({
            id: 'progressPercent',
            header: 'Tiến trình',
            enableSorting: false,
            size: 160,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const progress = row.original.progressPercent || 0
                return (
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-border rounded-md overflow-hidden">
                            <div
                                className="h-full bg-background-muted rounded-md transition-all"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <span className="text-sm text-foreground w-10 text-left">{progress}%</span>
                    </div>
                )
            },
        }),
        columnHelper.accessor('dueDate', {
            header: 'Hạn cuối',
            enableSorting: true,
            size: 140,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head text-center' },
            cell: ({ getValue, row }) => {
                const value = getValue()
                const isOverdue = row.original.isOverdue

                // Display UTC time directly without timezone conversion
                const dueDate = value ? dayjs.utc(value).format('DD/MM/YYYY HH:mm') : '-'

                return (
                    <span
                        className={cn('text-sm text-foreground', isOverdue && 'text-red-500 font-semibold')}
                        title={dueDate}
                    >
                        {dueDate}
                    </span>
                )
            },
        }),
    ]

    return (
        <div className="flex flex-col gap-4 max-lg:gap-3 max-lg:p-0 h-[calc(100vh-240px)] min-h-[500px] overflow-hidden">
            <Tabs className="shrink-0" selectedKey={activeTab} onSelectionChange={k => setActiveTab(k as any)}>
                <TabList className="w-fit">
                    <Tab id="all">{t('projects.tasks.tabs.all', { defaultValue: 'Tất cả' })}</Tab>
                    <Tab id="assigned_to_me">
                        {t('projects.tasks.tabs.assignedToMe', { defaultValue: 'Công việc được giao' })}
                    </Tab>
                    <Tab id="assigned_by_me">
                        {t('projects.tasks.tabs.assignedByMe', { defaultValue: 'Công việc đã giao' })}
                    </Tab>
                </TabList>
            </Tabs>

            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex gap-4 flex-wrap max-lg:gap-2">
                    <BsSearchField
                        autoTrim
                        className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                        containerClassName="max-lg:w-full"
                        placeholder={t('common.actions.search', { defaultValue: 'Tìm kiếm' })}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />

                    <FilterDropdown
                        placeholder={t('common.fields.textFormat')}
                        inputPlaceholder={t('common.fields.textFormat')}
                        options={textFormatOptions}
                        selectedValues={textFormatValues}
                        onChange={setTextFormatValues}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />

                    <FilterDropdown
                        placeholder={t('assignTask.labels.priorityLevel')}
                        inputPlaceholder={t('assignTask.labels.priorityLevel')}
                        options={priorityOptions}
                        selectedValues={priorityValues}
                        onChange={setPriorityValues}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                    <FilterDropdown
                        placeholder={t('assignTask.labels.assignees')}
                        inputPlaceholder={t('assignTask.labels.assignees')}
                        options={assigneeOptions}
                        selectedValues={assigneeValues}
                        onChange={setAssigneeValues}
                        setOpen={setIsAssigneeFilterOpen}
                        isLoading={isLoadingAssignees}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                    <FilterDropdown
                        placeholder={t('common.fields.status')}
                        inputPlaceholder={t('common.fields.status')}
                        options={statusOptions}
                        selectedValues={statusValues}
                        onChange={setStatusValues}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                </div>

                <div className="flex items-center gap-4 max-lg:w-full max-lg:gap-2">
                    <Button
                        hidden={!canExportProject}
                        variant="outline"
                        className="group gap-2 border-border-medium max-lg:w-full"
                        onPress={handleExportReport}
                        isDisabled={exportReportMutation.isPending}
                    >
                        {exportReportMutation.isPending ? (
                            <Loader2 className="size-4 animate-spin" />
                        ) : (
                            t('projects.list.exportReport')
                        )}
                    </Button>
                    {canManage && (
                        <Button
                            variant="outline"
                            className="gap-2 border-border-medium px-3 max-lg:w-full"
                            hidden={!canManage}
                            onPress={() => {
                                setAssignTaskMode('order')
                                setIsAssignTaskOpen(true)
                            }}
                        >
                            Đặt hàng công việc
                        </Button>
                    )}
                    {canManage && (
                        <Button
                            variant="default"
                            className="gap-2 bg-background-accent text-text-accent hover:bg-background-accent/90 max-lg:w-full"
                            hidden={!canManage}
                            onPress={() => {
                                setAssignTaskMode('assignment')
                                setIsAssignTaskOpen(true)
                            }}
                        >
                            <PlusIcon size={20} strokeWidth={1.5} />
                            Giao việc
                        </Button>
                    )}
                </div>
            </div>

            <DataTable
                data={filteredTasks}
                columns={columns}
                enableRowSelection={canInput}
                rowSelection={rowSelection}
                enableSorting={true}
                sorting={sorting}
                setSorting={setSorting}
                setRowSelection={setRowSelection}
                isLoading={isLoading}
                emptyString={t('common.emptyState')}
                getRowClassName={row => (row.original.isOverdue ? 'bg-background-overdue' : undefined)}
                containerClassName="flex-1 overflow-y-auto min-h-0"
            />

            <div className="shrink-0">
                <TableDataFooter
                    selectedCount={Object.keys(rowSelection).length}
                    onDelete={handleDeleteMultiple}
                    deleteDisabled={Object.keys(rowSelection).length === 0 || deleteMutation.isPending || !canInput}
                    totalCount={totalResults}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalPages={Math.ceil(totalResults / pageSize)}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={value => setPageSize(value || 10)}
                />
            </div>

            {canInput && (
                <AssignTaskDialog
                    projectId={id}
                    open={isAssignTaskOpen}
                    onOpenChange={setIsAssignTaskOpen}
                    mode={assignTaskMode}
                    isDisabledProjectField={true}
                />
            )}
            {selectedTask && (
                <TaskDetailDialog
                    open={isTaskDetailOpen}
                    onOpenChange={setIsTaskDetailOpen}
                    taskId={selectedTask?.id!}
                    onNavigateToTask={task => setSelectedTask(task)}
                    taskType={selectedTask.taskType}
                />
            )}

            <PreviewSearchItem />
        </div>
    )
}
